import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { formatCount } from './budgetLineKinds';
import type { ModalFeedback } from './gpuBudgetFeedback';
import { otherFieldErrors } from './gpuBudgetFeedback';

interface GpuBudgetNameModalProps {
  mode: 'create' | 'rename';
  /** The current name on rename, `''` on create. */
  initialName: string;
  /** `catalog.limits.name_max_length` — a hint only: the backend's `name` error decides. */
  nameMaxLength: number;
  /** Runs the write; the tab decides whether the modal closes. */
  onSubmit: (name: string) => Promise<ModalFeedback>;
  onClose: () => void;
}

/**
 * Create or rename a budget: one name field. No `maxLength` on the input — the browser counts UTF-16
 * units and spaces, while the backend counts characters after a Unicode trim and NFC — so the length
 * is a live hint ("Up to 100 characters"), and the backend's `name` error (length, control characters,
 * a duplicate) is shown under the field. Mounted only while open, so each opening starts clean.
 */
const GpuBudgetNameModal: React.FunctionComponent<GpuBudgetNameModalProps> = ({
  mode,
  initialName,
  nameMaxLength,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState(initialName);
  const [pending, setPending] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [alert, setAlert] = useState<string[]>([]);

  // Characters (code points), not UTF-16 units: close to the backend's count after its trim + NFC.
  const length = Array.from(name.trim().normalize('NFC')).length;
  const isBlank = name.trim() === '';
  const title = mode === 'create' ? 'Create a budget' : 'Rename the budget';
  const submitLabel = mode === 'create' ? 'Create' : 'Rename';

  const handleSubmit = async () => {
    if (pending || isBlank) {
      return;
    }
    setPending(true);
    setAlert([]);
    setNameError(null);
    const feedback = await onSubmit(name);
    if (feedback.close) {
      onClose();
      return;
    }
    setPending(false);
    setNameError(feedback.fieldErrors.name ?? null);
    setAlert([
      ...(feedback.alert !== null ? [feedback.alert] : []),
      ...otherFieldErrors(feedback.fieldErrors, ['name']),
    ]);
  };

  const helper =
    nameError !== null ? (
      <HelperTextItem variant="error">{nameError}</HelperTextItem>
    ) : (
      <HelperTextItem variant={length > nameMaxLength ? 'warning' : 'default'}>
        Up to {formatCount(nameMaxLength)} characters ({formatCount(length)} used).
      </HelperTextItem>
    );

  return (
    <Modal variant="small" isOpen onClose={pending ? undefined : onClose} aria-labelledby="gpu-budget-name-title">
      <ModalHeader
        title={title}
        labelId="gpu-budget-name-title"
        description={mode === 'rename' ? `Current name: ${initialName}` : undefined}
      />
      <ModalBody>
        {alert.length > 0 && (
          <Alert
            variant="danger"
            isInline
            title={mode === 'create' ? 'Could not create the budget' : 'Could not rename the budget'}
            className="pf-v6-u-mb-md"
          >
            {alert.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </Alert>
        )}
        <Form
          id="gpu-budget-name-form"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <FormGroup label="Budget name" isRequired fieldId="gpu-budget-name">
            <TextInput
              id="gpu-budget-name"
              value={name}
              onChange={(_event, value) => {
                setName(value);
                setNameError(null);
              }}
              validated={nameError !== null ? 'error' : 'default'}
              isDisabled={pending}
              aria-describedby="gpu-budget-name-helper"
            />
            <FormHelperText>
              <HelperText id="gpu-budget-name-helper">{helper}</HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="gpu-budget-name-form"
          isLoading={pending}
          isDisabled={pending || isBlank}
        >
          {submitLabel}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={pending}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GpuBudgetNameModal;
